<template>
  <div>
    <div class="flex justify-between items-center">
      <JoszakiHeading class="first-letter:capitalize">
        {{ question.title }}
      </JoszakiHeading>
      <span class="flex flex-row items-center gap-5 text-lg">
        <FavouriteButton
          :entity-type="FAVOURITE_ENTITY_TYPES.QUESTION"
          :entity-id="question.id"
          class="mt-2"
        />
        <IconComponent
          v-if="me && questionSubscriptionsLoaded"
          :icon="subscribeIcon"
          :class="{ 'animate-spin cursor-wait': isSubscribing }"
          class="cursor-pointer hover:text-primary w-5 h-5"
          @click.native="setSubscribe(!isSubscribed)"
        />
        <IconComponent
          v-if="me && me.isAdmin"
          icon="exclamation-circle"
          class="cursor-pointer hover:text-primary w-5 h-5"
          @click.native="openReportModal"
        />
      </span>
    </div>
    <QuestionPills v-if="$mq !== 'mobile'" :question="question" class="mb-2" />
    <JoszakiCard>
      <div class="grid grid-cols-[auto_1fr] gap-2">
        <UserAvatar
          :image="
            $helpers.getProfilePictureThumbnailUrl(
              question.person.profilePhotoId
            )
          "
        />
        <div>
          <p class="font-bold text-lg">
            {{ $t("questionPage.questionOf", { name: question.person.name }) }}
          </p>
          <p class="text-lg whitespace-pre-line">
            {{ question.body }}
          </p>
          <p class="text-xs font-normal text-black/50 text-right">
            {{ formatDate(question.created) }}
            <template v-if="question.updated">
              {{
                $t("questionPage.editedAt", {
                  date: formatDate(question.updated),
                })
              }}
            </template>
          </p>
        </div>
      </div>
    </JoszakiCard>
  </div>
</template>

<script>
import { mapState } from "pinia";
import QuestionPills from "./QuestionPills.vue";
import UserAvatar from "~/components/UserAvatar.vue";
import { useUserStore } from "~/stores/user";
import { usePageDataStore } from "~/stores/user/pageData";
import { differenceToNow } from "~/helpers/date";
import {
  MODAL_REPORT_QUESTION,
  useQuestionStore,
} from "~/stores/question/question";
import FavouriteButton from "~/components/_refactored/favourite/FavouriteButton.vue";
import { FAVOURITE_ENTITY_TYPES } from "~/composables/favourite/favouriteEntityTypes";

export default {
  name: "CardQuestionDetails",
  components: { UserAvatar, QuestionPills, FavouriteButton },
  props: {
    question: {
      type: Object,
      required: true,
    },
  },
  setup() {
    return {
      questionStore: useQuestionStore(),
      pageData: usePageDataStore(),
    };
  },
  data() {
    return {
      isSubscribing: false,
      FAVOURITE_ENTITY_TYPES,
    };
  },
  computed: {
    ...mapState(useUserStore, {
      me: "me",
    }),
    ...mapState(usePageDataStore, {
      subscribedQuestionIds: "subscribedQuestionIds",
      questionSubscriptionsLoaded: (store) => !!store.questions,
    }),
    isSubscribed() {
      return this.subscribedQuestionIds.includes(this.question.id);
    },
    subscribeIcon() {
      if (this.isSubscribing) {
        return "cog";
      }

      return this.isSubscribed ? "bell-slash" : "bell";
    },
  },
  serverPrefetch() {
    return this.pageData.load("questions");
  },
  beforeMount() {
    this.pageData.load("questions");
  },
  methods: {
    formatDate(date) {
      return differenceToNow(this, date);
    },
    async setSubscribe(isSubscribe = true) {
      if (this.isSubscribing) {
        return;
      }

      try {
        this.isSubscribing = true;

        if (isSubscribe) {
          await this.questionStore.subscribeToQuestion(
            Number(this.question.id)
          );
          this.pageData.addQuestionSubscription(this.question.id);
        } else {
          await this.questionStore.unsubscribeFromQuestion(
            Number(this.question.id)
          );
          this.pageData.removeQuestionSubscription(this.question.id);
        }

        this.$joszaki.toast({
          message: this.$t(
            `questionPage.${isSubscribe ? "" : "un"}subscribeSuccess`
          ),
          position: "is-top",
          type: "success",
        });
      } catch (e) {
        this.$joszaki.toast({
          message: this.$t(
            `questionPage.${isSubscribe ? "" : "un"}subscribeError`
          ),
          position: "is-top",
          type: "danger",
        });
      } finally {
        this.isSubscribing = false;
      }
    },
    openModal(modalId) {
      this.questionStore.openModal(modalId, {
        question: { ...this.question },
      });
    },
    openReportModal() {
      if (!this.me) {
        this.$joszaki.toast({
          message: this.$t("questionPage.onlyLoggedInCanReport"),
          position: "is-top",
          type: "info",
        });
        return;
      }

      this.openModal(MODAL_REPORT_QUESTION);
    },
  },
};
</script>
